import { useSignal } from "@preact/signals-react"
import { useCallback, useEffect } from "react"
import { postSubmit, getSubmit } from "@ui-seeker/utils"
import { useToast } from "@hooks"
import { BACKEND_APIS } from "@constants"
import { isOpenCreateSftpUserModal, sftpCreatedPassword } from "Pages/Admin/ExportConnections/@signals"

export default function useCreateSftpUser() {
	const username = useSignal("")
	const includeFormatOptions = useSignal(true)
	const fileFormat = useSignal("csv")
	const compression = useSignal("none")
	const company = useSignal(null)
	const uid = useSignal(null)
	const selectByUser = useSignal(false)
	const companiesOptions = useSignal([])
	const usersOptions = useSignal([])
	const isLoadingOptions = useSignal(false)
	const isLoading = useSignal(false)
	const { showToast } = useToast()

	useEffect(() => {
		isLoadingOptions.value = true
		Promise.all([
			getSubmit(`${BACKEND_APIS.MASTER_DATA}/admin/options/export-connections-companies`),
			getSubmit(`${BACKEND_APIS.MASTER_DATA}/admin/options/export-connections-users`),
		]).then(([companies, users]) => {
			companiesOptions.value = companies
			usersOptions.value = users
		}).finally(() => {
			isLoadingOptions.value = false
		})
	}, [])

	const onToggleSelectByUser = useCallback((checked) => {
		selectByUser.value = checked
		company.value = null
		uid.value = null
	}, [])

	const reset = useCallback(() => {
		username.value = ""
		includeFormatOptions.value = true
		fileFormat.value = "csv"
		compression.value = "none"
		company.value = null
		uid.value = null
		selectByUser.value = false
	}, [])

	const onSubmit = useCallback(async () => {
		if (!username.value.trim()) return
		isLoading.value = true

		let apiErrorMessage = "Error creating SFTP user"
		const request = postSubmit("/api/sftp-server/create-user", {
			username: username.value.trim(),
			include_format_options: includeFormatOptions.value,
			...(includeFormatOptions.value && {
				file_format: fileFormat.value,
				compression: compression.value,
				...(company.value != null && { company: company.value }),
				...(uid.value != null && { uid: uid.value }),
			}),
		}).catch(async (err) => {
			try {
				const body = await err.json()
				if (body?.error) apiErrorMessage = body.error
			} catch {}
			throw err
		})

		try {
			const res = await showToast.promise(request, {
				loading: "Creating SFTP user...",
				success: "SFTP user created successfully",
				error: "Error creating SFTP user",
				onError: () => apiErrorMessage,
			})
			reset()
			isOpenCreateSftpUserModal.value = false
			sftpCreatedPassword.value = res?.password ?? null
		} finally {
			isLoading.value = false
		}
	}, [])

	const onClose = useCallback(() => {
		reset()
		isOpenCreateSftpUserModal.value = false
	}, [])

	return {
		username,
		includeFormatOptions,
		fileFormat,
		compression,
		company,
		uid,
		selectByUser,
		companiesOptions: companiesOptions.value,
		usersOptions: usersOptions.value,
		isLoadingOptions: isLoadingOptions.value,
		isLoading: isLoading.value,
		onToggleSelectByUser,
		onSubmit,
		onClose,
	}
}